import React from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  CandlestickChart,
  Search,
} from '@mui/icons-material';
import {
  ACTION_FILTERS,
  EmptyState,
  ScoreBar,
  fmt,
  fmtBn,
  getDisplayText,
  getDisplayTone,
  tdSx,
  thSx,
} from './shared';
import { colors } from '../../theme';

function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return null;
  return sortDir === 'desc'
    ? <ArrowDownward sx={{ fontSize: 12, ml: 0.3 }} />
    : <ArrowUpward sx={{ fontSize: 12, ml: 0.3 }} />;
}

export default function LeaderboardSection({
  t,
  loading,
  hasScanResult,
  results,
  filteredResults,
  actionFilter,
  setActionFilter,
  searchTicker,
  setSearchTicker,
  handleSort,
  sortField,
  sortDir,
  openTickerDrawer,
}) {
  if (loading && !hasScanResult) {
    return (
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[72, 78, 84, 72, 60].map((width, index) => (
              <Skeleton key={index} variant="rounded" width={width} height={30} sx={{ borderRadius: 2 }} />
            ))}
          </Box>
          <Skeleton variant="rounded" width={180} height={34} sx={{ borderRadius: 2 }} />
        </Box>

        <TableContainer sx={{ borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[40, 70, null, null, 48, null, null, null, null, null, null, null, null, null].map((width, index) => (
                  <TableCell key={index} sx={{ ...thSx, ...(width ? { width } : {}) }}>
                    <Skeleton variant="text" width="60%" height={14} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 10 }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell sx={tdSx}><Skeleton variant="text" width={20} height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width={52} height={18} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width="70%" height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="rounded" width="80%" height={8} sx={{ borderRadius: 1 }} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width={28} height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="rounded" width={68} height={20} sx={{ borderRadius: 4 }} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="rounded" width={72} height={18} sx={{ borderRadius: 4 }} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width="60%" height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width="60%" height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width="60%" height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width="60%" height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width="60%" height={16} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width="50%" height={14} /></TableCell>
                  <TableCell sx={tdSx}><Skeleton variant="text" width="70%" height={14} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: { xs: 1, sm: 0 }, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { height: 0 } }}>
          {ACTION_FILTERS.map((filter) => {
            const count = filter.key === 'all' ? results.length : results.filter((row) => row.action_label === filter.key).length;
            const active = actionFilter === filter.key;
            const Icon = filter.icon;
            return (
              <Button
                key={filter.key}
                size="small"
                startIcon={<Icon sx={{ fontSize: 14 }} />}
                onClick={() => setActionFilter(filter.key)}
                sx={{
                  px: 1.5, py: 0.5, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none',
                  borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0,
                  bgcolor: active ? alpha(filter.color, 0.1) : 'transparent',
                  color: active ? filter.color : colors.gray[400],
                  border: active ? `1.5px solid ${alpha(filter.color, 0.3)}` : '1.5px solid transparent',
                  '&:hover': { bgcolor: alpha(filter.color, 0.08) },
                }}
              >
                {t(filter.tKey)} ({count})
              </Button>
            );
          })}
        </Box>
        <TextField
          size="small"
          placeholder={t('vpa.searchTicker')}
          value={searchTicker}
          onChange={(event) => setSearchTicker(event.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: colors.gray[300] }} /></InputAdornment>,
          }}
          sx={{ width: { xs: '100%', sm: 180 }, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.78rem' } }}
        />
      </Box>

      {filteredResults.length === 0 ? (
        <EmptyState Icon={CandlestickChart} title={t('vpa.noResults')} subtitle={t('vpa.noResultsSub')} />
      ) : (
        <TableContainer sx={{ borderRadius: 1.5, border: `1px solid ${colors.gray[200]}`, bgcolor: colors.background.paper, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, width: 40, textAlign: 'center' }}>#</TableCell>
                <TableCell sx={{ ...thSx, width: 70 }}>{t('vpa.table.ticker')}</TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('close')}>
                  {t('vpa.table.price')}<SortIcon field="close" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('vpa_score')}>
                  {t('vpa.table.vpaScore')}<SortIcon field="vpa_score" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', width: 48, '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('confluence')}>
                  {t('vpa.table.confluence')}<SortIcon field="confluence" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={thSx}>{t('vpa.table.action')}</TableCell>
                <TableCell sx={thSx}>{t('vpa.table.setup')}</TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('setup_score')}>
                  {t('vpa.table.setupScore')}<SortIcon field="setup_score" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('trend_score')}>
                  {t('vpa.table.trend')}<SortIcon field="trend_score" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('penalty_score')}>
                  {t('vpa.table.penalty')}<SortIcon field="penalty_score" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('trap_risk')}>
                  {t('vpa.table.trap')}<SortIcon field="trap_risk" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('vol_ratio')}>
                  {t('vpa.table.volRatio')}<SortIcon field="vol_ratio" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('rr_target_2')}>
                  {t('vpa.table.rr')}<SortIcon field="rr_target_2" sortField={sortField} sortDir={sortDir} />
                </TableCell>
                <TableCell sx={{ ...thSx, cursor: 'pointer', '&:hover': { color: colors.gray[600] } }} onClick={() => handleSort('avg_value_20d')}>
                  {t('vpa.table.value')}<SortIcon field="avg_value_20d" sortField={sortField} sortDir={sortDir} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults.map((row, idx) => {
                const tableDisplay = row.display?.table || {};
                const actionDisplay = tableDisplay.action || null;
                const setupDisplay = tableDisplay.setup || null;
                const confluenceDisplay = tableDisplay.confluence || null;
                const setupScoreDisplay = tableDisplay.setup_score || null;
                const trendScoreDisplay = tableDisplay.trend_score || null;
                const penaltyDisplay = tableDisplay.penalty_score || null;
                const trapDisplay = tableDisplay.trap_risk || null;
                const volRatioDisplay = tableDisplay.vol_ratio || null;
                const rrDisplay = tableDisplay.rr_target_2 || null;

                return (
                  <TableRow
                    key={row.ticker}
                    hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: colors.gray[50] } }}
                    onClick={() => openTickerDrawer(row)}
                  >
                    <TableCell sx={{ ...tdSx, textAlign: 'center', fontWeight: 700, color: colors.gray[400], fontSize: '0.72rem' }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: colors.gray[800] }}>{row.ticker}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: colors.gray[600], fontFamily: 'monospace' }}>
                        {fmt(row.close)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><ScoreBar score={row.vpa_score} /></TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title={getDisplayText(confluenceDisplay, 'sub', t, '')} arrow>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, fontFamily: 'monospace', color: getDisplayTone(confluenceDisplay?.tone).color }}>
                          {getDisplayText(confluenceDisplay, 'value', t)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title={getDisplayText(actionDisplay, 'sub', t, '')} arrow>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: getDisplayTone(actionDisplay?.tone).color }}>
                          {getDisplayText(actionDisplay, 'value', t)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title={row.setup || ''}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: getDisplayTone(setupDisplay?.tone).color }}>
                          {getDisplayText(setupDisplay, 'value', t)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: getDisplayTone(setupScoreDisplay?.tone).color, fontFamily: 'monospace' }}>
                        {getDisplayText(setupScoreDisplay, 'value', t)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: getDisplayTone(trendScoreDisplay?.tone).color, fontFamily: 'monospace' }}>
                        {getDisplayText(trendScoreDisplay, 'value', t)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: getDisplayTone(penaltyDisplay?.tone).color }}>
                        {getDisplayText(penaltyDisplay, 'value', t)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title={getDisplayText(trapDisplay, 'sub', t, '')}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: getDisplayTone(trapDisplay?.tone).color }}>
                          {getDisplayText(trapDisplay, 'value', t)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title={getDisplayText(volRatioDisplay, 'sub', t, '')}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, fontFamily: 'monospace', color: getDisplayTone(volRatioDisplay?.tone).color }}>
                          {getDisplayText(volRatioDisplay, 'value', t)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: getDisplayTone(rrDisplay?.tone).color }}>
                        {getDisplayText(rrDisplay, 'value', t)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.72rem', color: colors.gray[500], fontFamily: 'monospace' }}>
                        {fmtBn(row.avg_value_20d)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
